import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { formatDate, formatEuro, formatItInput, parseItNumber, numberToItInput } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import ParcelleSegnalatore from "./ParcelleSegnalatore";
import { displayName } from "../lib/anagConfig";
import { ReceiptText, Plus, Trash2, Pencil, Loader2, ArrowLeft, Save, Check } from "lucide-react";
import { toast } from "sonner";
import { SpettanzeSegnalatore } from "./SpettanzeSegnalatore";

const TIPI = ["MATERIALE", "LESIONI", "MATERIALE+LESIONE"];

const EuroInput = ({ value, onChange, testId }) => (
  <div className="relative mt-1">
    <Input type="text" inputMode="decimal" value={value} onChange={(e) => onChange(formatItInput(e.target.value))}
      onBlur={() => { if (String(value || "").trim() !== "") onChange(numberToItInput(parseItNumber(value))); }}
      placeholder="0,00" data-testid={testId} className="h-9 pr-8 bg-slate-900/60 border-slate-700 font-mono text-right" />
    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">€</span>
  </div>
);
const EMPTY = { tipo: "MATERIALE", sorte_capitale: "", acconto_ricevuto: "", data_acconto: "", onorari: "", spese_studio: "", quietanza_consegnata: false, onorari_ricevuti: false, data: "", data_incasso_onorari: "" };

export default function QuietanzeDialog({ open, onOpenChange, praticaId, client, onClientChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null=lista, {} o record=form
  const [form, setForm] = useState(EMPTY);
  const [segAbil, setSegAbil] = useState(false);
  const [segParc, setSegParc] = useState([]);
  const [segRec, setSegRec] = useState(null); // scheda anagrafica del segnalatore
  const base = `/studio/pratiche/${praticaId}/quietanze`;

  useEffect(() => {
    if (open && client) { setSegAbil(!!client.provvigione_abilitata); setSegParc(client.parcelle || []); }
  }, [open, client]);

  useEffect(() => {
    if (!open) { setSegRec(null); return; }
    const nome = (client?.segnalatore || "").trim();
    if (!nome) { setSegRec(null); return; }
    const norm = (s) => (s || "").toLowerCase().replace(/[.,;:'"()\-_/]+/g, " ").trim().replace(/\s+/g, " ");
    const sortTok = (s) => norm(s).split(" ").filter(Boolean).sort().join(" ");
    Promise.all([
      api.get(`/anagrafiche/segnalatori`).then((r) => r.data || []).catch(() => []),
      api.get(`/anagrafiche/agenzie`).then((r) => r.data || []).catch(() => []),
    ]).then(([s, ag]) => {
      const data = [...s, ...ag];
      const target = norm(nome), targetSorted = sortTok(nome);
      const rec = (data || []).find((r) => {
        const dn = norm(displayName(r));
        const nc = norm([r.nome, r.cognome].filter(Boolean).join(" "));
        const cn = norm([r.cognome, r.nome].filter(Boolean).join(" "));
        const den = norm(r.denominazione);
        const ab = norm(r.abbreviazione);
        if (dn === target || nc === target || cn === target || (den && den === target) || (ab && ab === target)) return true;
        if (den && sortTok(r.denominazione) === targetSorted) return true;
        const rs = sortTok([r.nome, r.cognome].filter(Boolean).join(" "));
        return rs && rs === targetSorted;
      });
      setSegRec(rec || null);
    }).catch(() => setSegRec(null));
  }, [open, client?.segnalatore]);

  // Provvigione automatica basata sulla scheda del segnalatore (Compenso: percentuale + base di calcolo)
  const diffAvere = (parseItNumber(form.sorte_capitale) || 0) - (parseItNumber(form.acconto_ricevuto) || 0);

  const provv = (() => {
    if (!segRec || (segRec.compenso_tipo || "fisso") !== "percentuale") return null;
    const pct = Number((String(segRec.compenso_percentuale || "").match(/\d+(?:[.,]\d+)?/) || ["0"])[0].replace(",", "."));
    if (!pct || pct <= 0) return null;
    const b = segRec.compenso_base;
    let baseAmt;
    if (b === "sugli onorari") baseAmt = Math.max(0, (parseItNumber(form.onorari) || 0) - (parseItNumber(form.spese_studio) || 0));
    else if (b === "sul risarcimento" || b === "sul danno") baseAmt = parseItNumber(form.sorte_capitale) || 0;
    else return null; // "altro" o non impostata → nessun calcolo automatico
    return { pct, base: b, amount: baseAmt * pct / 100 };
  })();
  const persistSeg = (patch) => onClientChange && onClientChange(patch);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(base); setRows(data); } catch { /* */ } finally { setLoading(false); }
  };
  useEffect(() => { if (open) { load(); setEditing(null); } /* eslint-disable-next-line */ }, [open]);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (q) => {
    setForm({ tipo: q.tipo || "MATERIALE", sorte_capitale: numberToItInput(q.sorte_capitale), acconto_ricevuto: numberToItInput(q.acconto_ricevuto), data_acconto: q.data_acconto || "", onorari: numberToItInput(q.onorari), spese_studio: numberToItInput(q.spese_studio), quietanza_consegnata: !!q.quietanza_consegnata, onorari_ricevuti: !!q.onorari_ricevuti, data: q.data || "", data_incasso_onorari: q.data_incasso_onorari || "" });
    setEditing(q);
  };
  const save = async () => {
    const payload = {
      tipo: form.tipo, sorte_capitale: parseItNumber(form.sorte_capitale), acconto_ricevuto: parseItNumber(form.acconto_ricevuto), data_acconto: form.data_acconto || null, onorari: parseItNumber(form.onorari),
      spese_studio: parseItNumber(form.spese_studio),
      quietanza_consegnata: form.quietanza_consegnata, onorari_ricevuti: form.onorari_ricevuti, data: form.data || null,
      data_incasso_onorari: form.data_incasso_onorari || null,
    };
    try {
      if (editing && editing.id) await api.patch(`${base}/${editing.id}`, payload);
      else await api.post(base, payload);
      setEditing(null); load(); toast.success("Quietanza salvata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`${base}/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const [toggling, setToggling] = useState(null);
  const toggleOnorari = async (q) => {
    const val = !q.onorari_ricevuti;
    setToggling(q.id);
    const payload = {
      tipo: q.tipo, sorte_capitale: q.sorte_capitale || 0, acconto_ricevuto: q.acconto_ricevuto || 0, data_acconto: q.data_acconto || null,
      onorari: q.onorari || 0, spese_studio: q.spese_studio || 0, quietanza_consegnata: !!q.quietanza_consegnata,
      onorari_ricevuti: val, data: q.data || null, data_incasso_onorari: q.data_incasso_onorari || null,
    };
    try {
      await api.patch(`${base}/${q.id}`, payload);
      setRows((rs) => rs.map((r) => (r.id === q.id ? { ...r, onorari_ricevuti: val } : r)));
      toast.success(val ? "Onorari segnati come ricevuti" : "Onorari segnati come non ricevuti");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setToggling(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-2xl" data-testid="quietanze-dialog">
        <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><ReceiptText className="h-5 w-5 text-amber-400" /> Gestione Quietanze</DialogTitle></DialogHeader>

        {editing === null ? (
          <>
            <div className="flex justify-end">
              <Button onClick={openNew} data-testid="quietanza-add" className="h-9 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold"><Plus className="h-4 w-4 mr-1" /> Aggiungi</Button>
            </div>
            <div className="mt-2 rounded-lg border border-slate-700 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-800/60 text-[11px] uppercase tracking-wider text-slate-400">
                    <th className="text-left px-3 py-2">Danno</th>
                    <th className="text-right px-3 py-2">Sorte Capitale</th>
                    <th className="text-right px-3 py-2">Acconto</th>
                    <th className="text-right px-3 py-2">Differenza</th>
                    <th className="text-right px-3 py-2" title="Il pallino verde indica onorari ricevuti: clicca per cambiare">Onorari (ricevuti)</th>
                    <th className="text-right px-3 py-2">Spese</th>
                    <th className="text-left px-3 py-2">Data</th>
                    <th className="text-left px-3 py-2">Data Incasso Onorari</th>
                    <th className="px-3 py-2 w-24"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {loading ? (
                    <tr><td colSpan={9} className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin text-amber-500 inline" /></td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={9} className="py-6 text-center text-slate-500">Nessuna quietanza.</td></tr>
                  ) : rows.map((q) => (
                    <tr key={q.id} className="hover:bg-slate-800/30" data-testid={`quietanza-row-${q.id}`}>
                      <td className="px-3 py-2.5 font-medium text-slate-100">{q.tipo}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-200">{formatEuro(q.sorte_capitale)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-300" data-testid={`quietanza-acconto-${q.id}`}>{formatEuro(q.acconto_ricevuto || 0)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-200" data-testid={`quietanza-differenza-${q.id}`}>{formatEuro((Number(q.sorte_capitale) || 0) - (Number(q.acconto_ricevuto) || 0))}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-200">
                        <span className="inline-flex items-center justify-end gap-1.5">
                          <button type="button" onClick={() => toggleOnorari(q)} disabled={toggling === q.id}
                            title={q.onorari_ricevuti ? "Onorari ricevuti — clicca per annullare" : "Onorari non ricevuti — clicca per segnarli come ricevuti"}
                            aria-pressed={!!q.onorari_ricevuti} data-testid={`quietanza-toggle-onorari-${q.id}`}
                            className={`inline-flex items-center justify-center h-6 w-6 rounded-full border transition-colors disabled:opacity-50 ${q.onorari_ricevuti ? "bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600" : "border-slate-500 text-transparent hover:border-emerald-500 hover:text-emerald-500/60"}`}>
                            {toggling === q.id ? <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-300" /> : <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                          </button>
                          {formatEuro(q.onorari)}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-300" data-testid={`quietanza-spese-${q.id}`}>{formatEuro(q.spese_studio)}</td>
                      <td className="px-3 py-2.5 font-mono text-slate-400">{q.data ? formatDate(q.data) : "—"}</td>
                      <td className="px-3 py-2.5 font-mono text-slate-400" data-testid={`quietanza-incasso-${q.id}`}>{q.data_incasso_onorari ? formatDate(q.data_incasso_onorari) : "—"}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => openEdit(q)} data-testid={`quietanza-edit-${q.id}`} title="Modifica" className="text-slate-300 hover:text-amber-400"><Pencil className="h-4 w-4" /></button>
                          <button onClick={() => del(q.id)} data-testid={`quietanza-del-${q.id}`} title="Elimina" className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {!loading && rows.length > 0 && (() => {
                  const totSorte = rows.reduce((s, q) => s + (Number(q.sorte_capitale) || 0), 0);
                  const totAcc = rows.reduce((s, q) => s + (Number(q.acconto_ricevuto) || 0), 0);
                  const totOnorari = rows.reduce((s, q) => s + (Number(q.onorari) || 0), 0);
                  const totSpese = rows.reduce((s, q) => s + (Number(q.spese_studio) || 0), 0);
                  return (
                    <tfoot className="border-t-2 border-amber-500/50">
                      <tr className="bg-amber-500/10 text-slate-100 font-semibold" data-testid="quietanze-totali-row">
                        <td className="px-3 py-2.5 uppercase text-[11px] tracking-wider text-amber-300">Totali</td>
                        <td className="px-3 py-2.5 text-right font-mono text-amber-200" data-testid="quietanze-tot-sorte">{formatEuro(totSorte)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-amber-200" data-testid="quietanze-tot-acconto">{formatEuro(totAcc)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-amber-200" data-testid="quietanze-tot-differenza">{formatEuro(totSorte - totAcc)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-amber-200" data-testid="quietanze-tot-onorari">{formatEuro(totOnorari)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-amber-200" data-testid="quietanze-tot-spese">{formatEuro(totSpese)}</td>
                        <td className="px-3 py-2.5" colSpan={3}>
                          <span className="inline-flex items-center gap-2">
                            <span className="text-[11px] uppercase tracking-wider text-slate-300">Totale complessivo</span>
                            <span className="font-mono font-bold text-amber-300 text-base" data-testid="quietanze-tot-complessivo">{formatEuro(totSorte - totAcc + totOnorari)}</span>
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  );
                })()}
              </table>
            </div>
            {!loading && <SpettanzeSegnalatore segnalatore={client?.segnalatore} segRec={segRec} rows={rows} />}
            {client?.segnalatore ? (
              <div className="mt-3">
                <ParcelleSegnalatore segnalatore={client.segnalatore} abilitata={segAbil} parcelle={segParc}
                  segCompenso={segRec ? { tipo: segRec.compenso_tipo || "fisso", perc: segRec.compenso_percentuale, base: segRec.compenso_base, importo: segRec.compenso_importo } : null}
                  quietanza={rows[0] ? { onorari: rows[0].onorari, sorte: rows[0].sorte_capitale } : null}
                  onAbilitataChange={(v) => { setSegAbil(v); persistSeg({ provvigione_abilitata: v }); }}
                  onParcelleChange={(arr) => { setSegParc(arr); persistSeg({ parcelle: arr }); }} />
              </div>
            ) : null}
          </>
        ) : (
          <div className="space-y-4" data-testid="quietanza-form">
            <div>
              <Label className="text-slate-300 text-xs">Tipo materiale</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger data-testid="quietanza-tipo" className="mt-1 h-9 bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
                  {TIPI.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 items-end">
              <div>
                <Label className="text-slate-300 text-xs">Data sorte capitale</Label>
                <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="quietanza-data" className="mt-1 h-9 bg-slate-900/60 border-slate-700" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">(1) Sorte Capitale (€)</Label>
                <EuroInput value={form.sorte_capitale} onChange={(v) => setForm((f) => ({ ...f, sorte_capitale: v }))} testId="quietanza-sorte" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Data acconto</Label>
                <Input type="date" value={form.data_acconto} onChange={(e) => setForm({ ...form, data_acconto: e.target.value })} data-testid="quietanza-data-acconto" className="mt-1 h-9 bg-slate-900/60 border-slate-700" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">(2) Acconto ricevuto (€)</Label>
                <EuroInput value={form.acconto_ricevuto} onChange={(v) => setForm((f) => ({ ...f, acconto_ricevuto: v }))} testId="quietanza-acconto" />
              </div>
              <div />
              <div>
                <Label className="text-slate-300 text-xs">(3) Differenza avere (€)</Label>
                <div className="mt-1 h-9 rounded-md border border-slate-700 bg-slate-800/60 px-3 flex items-center justify-end font-mono text-slate-100" data-testid="quietanza-differenza">{formatEuro(diffAvere)}</div>
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Data Incasso Onorari</Label>
                <Input type="date" value={form.data_incasso_onorari} onChange={(e) => setForm({ ...form, data_incasso_onorari: e.target.value })} data-testid="quietanza-data-incasso" className="mt-1 h-9 bg-slate-900/60 border-slate-700" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">(4) Onorari (€)</Label>
                <EuroInput value={form.onorari} onChange={(v) => setForm((f) => ({ ...f, onorari: v }))} testId="quietanza-onorari" />
              </div>
            </div>
            <div data-testid="quietanza-spese-wrap">
              <Label className="text-slate-300 text-xs">Spese sostenute dallo studio (€)</Label>
              <EuroInput value={form.spese_studio} onChange={(v) => setForm((f) => ({ ...f, spese_studio: v }))} testId="quietanza-spese" />
              <p className="mt-1 text-[11px] text-slate-500">Campo informativo: non modifica il Totale. Viene sottratto dagli onorari nel calcolo della provvigione segnalatore.</p>
            </div>
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 flex items-center justify-between" data-testid="quietanza-totale-box">
              <span className="text-sm font-semibold text-slate-100">(5) Totale (€) <span className="text-[11px] font-normal text-slate-400">differenza avere + onorari</span></span>
              <span className="font-mono font-bold text-lg text-amber-300" data-testid="quietanza-totale">{formatEuro(diffAvere + (parseItNumber(form.onorari) || 0))}</span>
            </div>
            {provv && (
              <div className="rounded-md border border-teal-500/30 bg-teal-500/[0.06] px-3 py-2 text-sm flex items-center justify-between" data-testid="provvigione-calcolo">
                <span className="text-slate-300">Provvigione segnalatore{client?.segnalatore ? ` · ${client.segnalatore}` : ""} ({provv.pct}% {provv.base})</span>
                <span className="font-mono font-semibold text-teal-300">{formatEuro(provv.amount)}</span>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={form.quietanza_consegnata} onCheckedChange={(v) => setForm({ ...form, quietanza_consegnata: !!v })} data-testid="quietanza-consegnata" />
                <span className="text-sm text-slate-200">Quietanza consegnata</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={form.onorari_ricevuti} onCheckedChange={(v) => setForm({ ...form, onorari_ricevuti: !!v })} data-testid="quietanza-onorari-ricevuti" />
                <span className="text-sm text-slate-200">Onorari ricevuti</span>
              </label>
            </div>
            <div className="flex justify-between gap-2 pt-2 border-t border-slate-800">
              <Button variant="outline" onClick={() => setEditing(null)} data-testid="quietanza-back" className="border-slate-700 text-slate-200 hover:bg-slate-800"><ArrowLeft className="h-4 w-4 mr-1" /> Indietro</Button>
              <Button onClick={save} data-testid="quietanza-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold"><Save className="h-4 w-4 mr-1" /> Salva</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
